import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";

const EASE = [0.22, 1, 0.36, 1];

const REVEAL = { opacity: 0, y: 30 };
const REVEAL_LG = { opacity: 0, y: 40 };
const REVEAL_IN = { opacity: 1, y: 0 };
const VIEWPORT = { once: true, margin: "-80px" };
const VIEWPORT_CARDS = { once: true, margin: "-60px" };
const TRANS = { duration: 0.8, ease: EASE };

const CARD_IMAGES = {
  manual: "/icon-manual.png?v=2",
  powered: "/icon-powered.png?v=1",
  bathroom: "/icon-bathroom.png?v=1",
  beds: "/icon-beds.png?v=1",
  aids: "/icon-aids.png?v=1",
};

export default function Products() {
  const { t } = useLang();
  const navigate = useNavigate();

  return (
    <section id="products" className="relative overflow-hidden bg-[#F9FAFB] py-28 lg:py-40" data-testid="products-section">
      {/* subtle curved tech lines, echoing the hero */}
      <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        {[0, 26, 52, 78, 104].map((o, i) => (
          <path
            key={o}
            d={`M -100 ${240 + o} C ${420 - o} ${120 + o}, ${900 - o} ${520 + o}, 1600 ${300 - o}`}
            fill="none"
            stroke={i === 2 ? "#F06B24" : "#0B2C57"}
            strokeOpacity={i === 2 ? 0.28 : 0.05}
            strokeWidth={i === 2 ? 1.2 : 1}
          />
        ))}
      </svg>

      <div className="relative z-10 max-w-[1800px] mx-auto px-6 sm:px-8 md:px-12 lg:px-20">
        {/* Header */}
        <motion.div
          initial={REVEAL}
          whileInView={REVEAL_IN}
          viewport={VIEWPORT}
          transition={TRANS}
          className="mb-16 lg:mb-24 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 lg:gap-20 text-center lg:text-left"
        >
          <div className="lg:max-w-3xl">
            <p className="text-xs font-bold tracking-[0.28em] uppercase text-mobix-orange mb-5">{t.products.title}</p>
            <h2 className="font-extrabold uppercase tracking-tight text-mobix-navy text-[clamp(2rem,4.8vw,3.75rem)] leading-[1.0]" data-testid="products-title">
              {t.products.heading}<span className="text-mobix-orange">.</span>
            </h2>
          </div>
          <div className="mx-auto lg:mx-0 lg:pb-2 max-w-md lg:max-w-[18rem]">
            <p className="text-base font-medium text-mobix-navy leading-relaxed">
              {t.products.subtitle}
            </p>
          </div>
        </motion.div>

        {/* 3 x 2 grid: 5 product cards + View All */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {t.products.cards.map((card, i) => (
            <motion.button
              key={card.key}
              initial={REVEAL_LG}
              whileInView={REVEAL_IN}
              viewport={VIEWPORT_CARDS}
              transition={{ duration: 0.7, ease: EASE, delay: (i % 3) * 0.08 }}
              onClick={() => navigate(`/products/${card.key}`)}
              data-testid={`product-card-${card.key}`}
              className="group relative flex flex-col items-start text-left rounded-3xl bg-white border border-white/70 p-8 lg:p-10 min-h-[19rem] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_28px_60px_rgba(11,43,86,0.12)]"
            >
              <div className="h-24 w-full flex items-center">
                <img src={CARD_IMAGES[card.key]} alt="" className="h-20 w-auto object-contain object-left" />
              </div>
              <h3 className="mt-6 text-xl lg:text-2xl font-bold tracking-tight text-mobix-navy">
                <span className="border-b-2 border-transparent pb-0.5 transition-colors duration-300 group-hover:border-mobix-orange">
                  {card.name}
                </span>
              </h3>
              <p className="mt-3 text-sm lg:text-base text-mobix-navy/60 leading-relaxed">{card.desc}</p>
              <ArrowUpRight
                size={22}
                className="absolute top-8 right-8 text-mobix-mauve/40 transition-all duration-300 group-hover:text-mobix-orange group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </motion.button>
          ))}

          {/* View All special card */}
          <motion.button
            initial={REVEAL_LG}
            whileInView={REVEAL_IN}
            viewport={VIEWPORT_CARDS}
            transition={{ duration: 0.7, ease: EASE, delay: 0.16 }}
            onClick={() => navigate("/products")}
            data-testid="product-card-view-all"
            className="group relative flex flex-col items-center justify-center text-center rounded-3xl bg-mobix-navy p-8 lg:p-10 min-h-[19rem] transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_28px_60px_rgba(11,43,86,0.30)]"
          >
            <span className="mb-6 inline-flex h-24 w-24 items-center justify-center rounded-full bg-white/[0.06] group-hover:bg-white/[0.12] transition-colors duration-300">
              <ArrowRight size={30} className="text-mobix-orange transition-transform duration-300 group-hover:translate-x-1" />
            </span>
            <h3 className="text-3xl lg:text-4xl font-extrabold tracking-tight text-white">
              {t.products.viewAll}
            </h3>
            <p className="mt-3 max-w-[16rem] text-sm lg:text-base text-white/60 leading-relaxed">
              {t.products.viewAllDesc}
            </p>
          </motion.button>
        </div>
      </div>
    </section>
  );
}
